"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ReactNode } from "react";
import {
	Checkbox as RACCheckbox,
	type CheckboxProps as RACCheckboxProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

const boxStyles = cva(
	[
		"shrink-0 relative flex items-center justify-center",
		"border-[1.4px] border-solid",
		"rounded-component-specific-checkbox",
		"transition-colors duration-200",
		"bg-background-bg-neutral-1",
	],
	{
		variants: {
			size: {
				small: "size-[18px]",
				large: "size-6",
			},
			tone: {
				default: "",
				error: "bg-status-error-bg",
			},
			state: {
				default: "",
				hover: "",
				disabled: "",
				selected: "",
				selectedHover: "",
				selectedDisabled: "",
			},
		},
		compoundVariants: [
			{ tone: "default", state: "default", className: "border-border-medium" },
			{ tone: "default", state: "hover", className: "border-brand-blue-60" },
			{ tone: "default", state: "disabled", className: "border-border-light" },
			{ tone: "default", state: "selected", className: "border-button-solid-primary-default-bg" },
			{ tone: "default", state: "selectedHover", className: "border-brand-blue-60" },
			{ tone: "default", state: "selectedDisabled", className: "border-border-light" },
			{ tone: "error", state: "default", className: "border-status-error-surface" },
			{ tone: "error", state: "hover", className: "border-status-error-surface" },
			{ tone: "error", state: "disabled", className: "border-status-error-surface" },
			{ tone: "error", state: "selected", className: "border-status-error-surface" },
			{ tone: "error", state: "selectedHover", className: "border-status-error-surface" },
			{ tone: "error", state: "selectedDisabled", className: "border-status-error-surface" },
		],
		defaultVariants: { size: "large", tone: "default", state: "default" },
	},
);

const innerStyles = cva("rounded-general-sm transition-colors duration-200", {
	variants: {
		size: {
			small: "size-3",
			large: "size-4",
		},
		tone: {
			default: "",
			error: "bg-status-error-surface",
		},
		state: {
			default: "",
			hover: "",
			disabled: "",
		},
	},
	compoundVariants: [
		{ tone: "default", state: "default", className: "bg-button-solid-primary-default-bg" },
		{ tone: "default", state: "hover", className: "bg-brand-blue-60" },
		{ tone: "default", state: "disabled", className: "bg-neutral-black-20" },
		{ tone: "error", state: "default", className: "bg-status-error-surface" },
		{ tone: "error", state: "hover", className: "bg-status-error-surface" },
		{ tone: "error", state: "disabled", className: "bg-status-error-surface" },
	],
	defaultVariants: { size: "large", tone: "default", state: "default" },
});

const labelStyles = cva("font-body font-regular leading-[1.5] min-w-full", {
	variants: {
		size: {
			small: "text-xs",
			large: "text-sm",
		},
		tone: {
			default: "text-text-dark-text-1",
			disabled: "text-text-dark-text-2",
			error: "text-status-error-text",
		},
	},
	defaultVariants: { size: "large", tone: "default" },
});

const descriptionStyles = cva("font-body font-regular leading-[1.5] min-w-full", {
	variants: {
		size: {
			small: "text-2xs",
			large: "text-xs",
		},
		tone: {
			default: "text-text-dark-text-2",
			disabled: "text-text-dark-text-2",
			error: "text-status-error-text",
		},
	},
	defaultVariants: { size: "large", tone: "default" },
});

const messageStyles = cva("font-body font-regular leading-[1.5] min-w-full", {
	variants: {
		size: {
			small: "text-2xs",
			large: "text-xs",
		},
		tone: {
			error: "text-status-error-text",
			success: "text-status-success-text",
		},
	},
	defaultVariants: { size: "large", tone: "error" },
});

type BoxVariants = VariantProps<typeof boxStyles>;

export type CheckboxProps = Omit<RACCheckboxProps, "children" | "className"> & {
	size?: NonNullable<BoxVariants["size"]>;
	description?: ReactNode;
	errorMessage?: ReactNode;
	successMessage?: ReactNode;
	isSuccess?: boolean;
	children?: ReactNode;
	className?: string;
	boxClassName?: string;
};

export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
	(
		{
			size = "large",
			description,
			errorMessage,
			successMessage,
			isInvalid,
			isSuccess = false,
			children,
			className,
			boxClassName,
			...checkboxProps
		},
		ref,
	) => {
		return (
			<RACCheckbox
				ref={ref}
				isInvalid={isInvalid}
				{...checkboxProps}
				className={twMerge(
					"flex gap-2-xs items-center w-full cursor-pointer outline-none data-[disabled]:cursor-not-allowed",
					className,
				)}
			>
				{({ isSelected, isHovered, isDisabled, isFocusVisible }) => {
					const tone: "default" | "error" = isInvalid ? "error" : "default";
					const labelTone: "default" | "error" | "disabled" = isInvalid
						? "error"
						: isDisabled
							? "disabled"
							: "default";

					const state = isDisabled
						? isSelected
							? "selectedDisabled"
							: "disabled"
						: isSelected
							? isHovered
								? "selectedHover"
								: "selected"
							: isHovered
								? "hover"
								: "default";

					const innerState = isDisabled ? "disabled" : isHovered ? "hover" : "default";

					return (
						<>
							<div
								className={twMerge(
									boxStyles({ size, tone, state }),
									isFocusVisible && "outline-2 outline-status-accessibility-focus outline-offset-2",
									boxClassName,
								)}
							>
								{isSelected && <div className={innerStyles({ size, tone, state: innerState })} />}
							</div>
							<div className="flex flex-1 min-w-0 flex-col gap-5-xs items-start">
								{children != null && (
									<span className={labelStyles({ size, tone: labelTone })}>{children}</span>
								)}
								{description != null && (
									<span className={descriptionStyles({ size, tone: labelTone })}>
										{description}
									</span>
								)}
								{tone === "error" && errorMessage != null ? (
									<span className={messageStyles({ size, tone: "error" })}>{errorMessage}</span>
								) : !isInvalid && isSuccess && successMessage != null ? (
									<span className={messageStyles({ size, tone: "success" })}>{successMessage}</span>
								) : null}
							</div>
						</>
					);
				}}
			</RACCheckbox>
		);
	},
);

Checkbox.displayName = "Checkbox";
